import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button.jsx";
import { cn } from "@/lib/utils.js";

const RCA_APPLY_SUCCESS_FLASH_MS = 1400;

/**
 * Brief success flash for Apply actions (green checkmark, then revert).
 * @param {number} [durationMs]
 */
function useApplySuccessFlash(durationMs = RCA_APPLY_SUCCESS_FLASH_MS) {
  const [show, setShow] = useState(false);
  const timeoutRef = useRef(
    /** @type {ReturnType<typeof setTimeout> | null} */ (null)
  );

  useEffect(
    () => () => {
      if (timeoutRef.current != null) clearTimeout(timeoutRef.current);
    },
    []
  );

  function trigger() {
    setShow(true);
    if (timeoutRef.current != null) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setShow(false);
      timeoutRef.current = null;
    }, durationMs);
  }

  return { show, trigger };
}

/**
 * Runs `onApply` and flashes success unless it returns `false`.
 * @param {() => boolean | void} onApply
 */
export function useRcaApplyAction(onApply) {
  const flash = useApplySuccessFlash();

  function applyAndFlash() {
    if (onApply() === false) return;
    flash.trigger();
  }

  return { showSuccess: flash.show, applyAndFlash };
}

/**
 * @param {{
 *   showSuccess: boolean,
 *   onClick: () => void,
 *   className?: string,
 *   disabled?: boolean,
 * }} props
 */
export default function RcaApplyButton({
  showSuccess,
  onClick,
  className,
  disabled = false,
}) {
  return (
    <Button
      type='button'
      size='sm'
      variant='outline'
      disabled={disabled}
      className={cn(
        "h-8 min-w-[4.25rem] shrink-0 px-2.5 text-xs transition-colors",
        showSuccess
          && "border-green-500/50 bg-green-50 text-green-800 hover:bg-green-50 hover:text-green-800 dark:bg-green-500/15 dark:text-green-300 dark:hover:bg-green-500/15 dark:hover:text-green-300",
        className
      )}
      onClick={onClick}
      aria-live='polite'
      aria-label={showSuccess ? "Applied" : "Apply"}>
      {showSuccess ? (
        <Check
          className='h-3.5 w-3.5 text-green-600 dark:text-green-400 animate-in zoom-in-50 fade-in duration-200 motion-reduce:animate-none'
          strokeWidth={2.5}
          aria-hidden
        />
      ) : (
        "Apply"
      )}
    </Button>
  );
}
